/**
 * Optional browser handoff after APT web login.
 * Primary OfficeAPT auth is in-app (email/password/MFA) — this path is for deep-link / Expo auth sessions.
 */
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Container, Typography, Button, Alert } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAuthUrl } from '../utils/subdomainUtils';
import {
  isAllowedOfficeaptAuthReturnUrl,
  OFFICEAPT_DEFAULT_AUTH_RETURN,
} from '../../shared/officeapt/authReturnUrl';
import { authFetch } from '../contexts/auth/authFetch';
import SEOHead from '../components/seo/SEOHead';

const RETURN_STORAGE_KEY = 'officeapt.auth.return';

function resolveReturnUrl(searchReturn: string | null): string {
  if (searchReturn && isAllowedOfficeaptAuthReturnUrl(searchReturn)) {
    try {
      sessionStorage.setItem(RETURN_STORAGE_KEY, searchReturn);
    } catch {
      /* ignore */
    }
    return searchReturn;
  }
  try {
    const stored = sessionStorage.getItem(RETURN_STORAGE_KEY);
    if (stored && isAllowedOfficeaptAuthReturnUrl(stored)) {
      return stored;
    }
  } catch {
    /* ignore */
  }
  return OFFICEAPT_DEFAULT_AUTH_RETURN;
}

function buildReturnWithCode(returnUrl: string, code: string): string {
  try {
    const u = new URL(returnUrl);
    u.searchParams.set('code', code);
    return u.toString();
  } catch {
    const join = returnUrl.includes('?') ? '&' : '?';
    return `${returnUrl}${join}code=${encodeURIComponent(code)}`;
  }
}

const OfficeaptAuthBridgePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isInitializing, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [manualHref, setManualHref] = useState<string | null>(null);
  const [stuckHint, setStuckHint] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setStuckHint(true), 12_000);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isInitializing || isLoading) return;

    const returnUrl = resolveReturnUrl(searchParams.get('return'));

    if (!isAuthenticated || !user) {
      const loginUrl = getAuthUrl('/login', {
        includeRedirect: true,
        redirectUrl: '/officeapt/auth',
      });
      window.location.replace(loginUrl);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch<{ code?: string }>('/api/auth/officeapt/handoff', {
          method: 'GET',
        });
        if (!res.success || !res.data?.code) {
          throw new Error(res.error || res.message || 'Could not create OfficeAPT handoff');
        }
        if (cancelled) return;
        try {
          sessionStorage.removeItem(RETURN_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        const target = buildReturnWithCode(returnUrl, res.data.code);
        setManualHref(target);
        window.location.replace(target);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Handoff failed');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isInitializing, isLoading, isAuthenticated, user, searchParams]);

  return (
    <>
      <SEOHead
        title="OfficeAPT Sign-in"
        description="Complete sign-in to continue in OfficeAPT"
        noIndex
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              background: 'rgba(255,255,255,0.96)',
              borderRadius: 3,
              p: 4,
              border: '1px solid rgba(255,255,255,0.25)',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="overline"
              sx={{ fontWeight: 700, letterSpacing: 1.2, color: 'primary.main' }}
            >
              OfficeAPT
            </Typography>
            {error ? (
              <Alert severity="error" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
                {error}
              </Alert>
            ) : (
              <Box sx={{ mt: 2 }}>
                <CircularProgress size={36} sx={{ mb: 2 }} />
                <Typography variant="h6" fontWeight={700}>
                  Returning to OfficeAPT…
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Completing secure handoff after APT sign-in.
                </Typography>
                {stuckHint ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Still waiting? Prefer signing in inside the OfficeAPT app (email + password) —
                    that path does not load this page.
                  </Typography>
                ) : null}
              </Box>
            )}
            {manualHref ? (
              <Button href={manualHref} variant="contained" fullWidth sx={{ mt: 2 }}>
                Open OfficeAPT
              </Button>
            ) : null}
            {error ? (
              <Button href="/login" variant="outlined" fullWidth sx={{ mt: 2 }}>
                Back to Sign In
              </Button>
            ) : null}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default OfficeaptAuthBridgePage;
